import { ReactNode, useState } from 'react';
import { motion } from 'framer-motion';
import { IdCardLanyard, Lock } from 'lucide-react';
import { useGameStore } from '../../../stores/gameStore';
import { Badge } from '../../ui/Badge';
import { playSound } from '../../../utils/soundService';

export interface SidebarItem {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
  active?: boolean;
}

interface SidebarProps {
  items: SidebarItem[];
  onItemClick?: (itemId: string) => void;
}

export function Sidebar({ items, onItemClick }: SidebarProps) {
  const { sidebarLocked, badgeActivated } = useGameStore();
  const [badgeOpen, setBadgeOpen] = useState(false);

  const handleBadgeOpen = () => {
    playSound('click');
    setBadgeOpen(true);
  };

  const handleBadgeClose = () => {
    setBadgeOpen(false);
  };

  return (
    <div className="w-12 bg-white border-r border-gray-200 flex flex-col items-center py-6 gap-3 h-full">
      {/* Application icons */}
      {items.map((item) => (
        <div key={item.id} className="relative">
          <button
            onClick={() => onItemClick?.(item.id)}
            className={`w-12 h-12 flex items-center justify-center rounded text-xl transition-all hover:bg-gray-200 ${
              item.active ? 'bg-gray-200' : ''
            }`}
            title={item.label}
          >
            {item.icon}
          </button>
          {sidebarLocked.includes(item.id) && (
            <div className="absolute top-2 right-2 bg-white rounded-full">
              <Lock size={12} strokeWidth={4} className="text-gray-800" />
            </div>
          )}
        </div>
      ))}

      <div className="flex-1" />

      {/* Badge icon at bottom */}
      <button
        onClick={handleBadgeOpen}
        className="w-12 h-12 flex items-center justify-center rounded transition-all text-gray-600 hover:bg-gray-200"
        title="Badge"
      >
        <motion.div
          animate={{
            scale: badgeActivated ? [1, 1.15, 1] : 1,
          }}
          transition={
            badgeActivated
              ? {
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }
              : { duration: 0.3 }
          }
        >
          <IdCardLanyard
            size={24}
            strokeWidth={1.5}
            className={badgeActivated ? 'text-gray-600' : 'text-gray-400'}
          />
        </motion.div>
      </button>

      {/* Badge Dialog */}
      <Badge isOpen={badgeOpen} onClose={handleBadgeClose} />
    </div>
  );
}
